import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLUE_COIN_IMG = 'https://customer-assets-eiarnc6j.emergentagent.net/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/n7x3phd0_image.png';

const ENTRY_LABELS = {
  admin_adjust: 'Ajuste da equipe',
  checkin: 'Check-in diário',
  roleta: 'Roleta',
  intercambio: 'Intercâmbio',
  campanha: 'Campanha',
  evento: 'Evento',
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso || '-'; }
};

/** Carteira de Azulzinhas 💙 — usada no painel do cliente e do afiliado.
 *  Somente leitura: quem credita/debita é o servidor. */
export const BlueCoinsCard = ({ meUrl, ledgerUrl, tokenKey, refreshKey = 0 }) => {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem(tokenKey)}`,
  }), [tokenKey]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}${meUrl}`, { headers: headers() })
      .then((r) => setBalance(r.data.balance))
      .catch(() => setBalance(0));
    setLedger([]);
  }, [meUrl, headers, refreshKey]);

  const toggleLedger = async () => {
    const next = !open;
    setOpen(next);
    if (next && ledger.length === 0) {
      setLoadingLedger(true);
      try {
        const r = await axios.get(`${BACKEND_URL}${ledgerUrl}?limit=50`, { headers: headers() });
        setLedger(r.data || []);
      } catch { /* silencioso */ }
      setLoadingLedger(false);
    }
  };

  return (
    <Card data-testid="blue-coins-card"
      className="rounded-2xl border border-sky-200/70 bg-gradient-to-br from-sky-50 via-white to-blue-50 shadow-[0_8px_32px_rgba(14,116,214,0.10)]">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={BLUE_COIN_IMG} alt="Azulzinhas"
              className="h-12 w-12 flex-shrink-0 object-contain drop-shadow-md" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-600">Azulzinhas</p>
              <p className="font-display text-2xl font-black text-blue-700" data-testid="blue-coins-balance">
                {balance === null
                  ? <Loader2 className="inline h-5 w-5 animate-spin" />
                  : balance.toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={toggleLedger} data-testid="blue-coins-toggle-ledger"
            className="rounded-full border-sky-200 bg-white/80 text-xs text-sky-700 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-sky-50">
            Extrato {open ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Moedinhas para eventos e atividades do site — em breve novas formas de ganhar!
        </p>

        {open && (
          <div className="custom-scrollbar mt-3 max-h-64 overflow-y-auto rounded-xl border border-sky-100 bg-white/70 pr-1"
            data-testid="blue-coins-ledger">
            {loadingLedger ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
            ) : ledger.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-500" data-testid="blue-coins-ledger-empty">
                Nenhuma movimentação ainda.
              </p>
            ) : ledger.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 border-b border-sky-50 px-3 py-2.5 last:border-0">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-slate-700">
                    {ENTRY_LABELS[e.entry_type] || e.entry_type}
                    {e.description ? ` · ${e.description}` : ''}
                  </p>
                  <p className="text-[10px] text-slate-400">{fmtDate(e.created_at)}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  {e.status === 'failed_insufficient' ? (
                    <span className="text-[10px] font-bold text-rose-500">não efetuado</span>
                  ) : (
                    <p className={`text-sm font-black ${e.direction === 'credit' ? 'text-sky-600' : 'text-rose-500'}`}>
                      {e.direction === 'credit' ? '+' : '−'}{e.amount.toLocaleString('pt-BR')}
                    </p>
                  )}
                  {e.balance_after !== null && e.status === 'completed' && (
                    <p className="text-[10px] text-slate-400">saldo {e.balance_after.toLocaleString('pt-BR')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlueCoinsCard;
